import { httpTransport, syncOnce } from '@makila/pos-core';
import { API, type DeviceState, refreshDevice } from './device';
import type { IdbTerminalStore } from './idb-store';

export interface SyncStatus {
  online: boolean;
  syncing: boolean;
  pending: number;
  lastSyncAt: string | null;
  /** Documentos que a nuvem marcou para revisão (assinatura/encadeamento). */
  review: number;
  error: string | null;
  /**
   * O browser garantiu que não apaga os dados do terminal por falta de espaço
   * (navigator.storage.persist). false = pode apagar — com vendas por enviar,
   * avisa-se. null = sem a API (ex.: WebView), nada a dizer.
   */
  storageProtected: boolean | null;
}

/**
 * Pede armazenamento persistente (AUD-23). Sem ele, o Chrome pode apagar o IndexedDB
 * do terminal quando o dispositivo fica sem espaço — e com ele as vendas assinadas
 * ainda por enviar. Concede-se sozinho a uma aplicação instalada.
 */
export async function protectStorage(): Promise<boolean | null> {
  // Na aplicação Android (WebView) os dados são da própria aplicação: o aviso "instale" não se aplica.
  if (typeof window !== 'undefined' && 'MakilaAndroid' in window) return null;
  const st = (navigator as Navigator & { storage?: StorageManager }).storage;
  if (!st?.persist || !st.persisted) return null;
  try { return (await st.persisted()) || (await st.persist()); } catch { return null; }
}

const EVERY_MS = 20_000;
/** Restaurante: as mesas e a cozinha precisam de ver as comandas das outras caixas depressa. */
const RESTAURANT_EVERY_MS = 5_000;
const REFRESH_DEVICE_MS = 10 * 60_000;

/**
 * Sincronização em segundo plano: a cada 20 s com rede (5 s num restaurante), logo que a rede
 * volta, e a pedido (depois de cada venda). Nunca bloqueia a venda: corre ao
 * lado, e uma falha só se mostra na barra de estado.
 */
export function startSync(store: IdbTerminalStore, getDevice: () => DeviceState, onDevice: (d: DeviceState) => void, onStatus: (s: SyncStatus) => void) {
  let status: SyncStatus = { online: navigator.onLine, syncing: false, pending: 0, lastSyncAt: null, review: 0, error: null, storageProtected: null };
  let running = false;
  let again = false;
  let lastRefresh = 0;
  const emit = (p: Partial<SyncStatus>) => { status = { ...status, ...p }; onStatus(status); };
  void protectStorage().then((storageProtected) => emit({ storageProtected }));

  const run = async () => {
    if (running) { again = true; return; }
    running = true;
    try {
      do {
        again = false;
        emit({ pending: await store.pendingCount(), online: navigator.onLine });
        if (!navigator.onLine) break;
        emit({ syncing: true });
        try {
          const device = getDevice();
          if (Date.now() - lastRefresh > REFRESH_DEVICE_MS) { onDevice(await refreshDevice(store, device)); lastRefresh = Date.now(); }
          const r = await syncOnce(store, httpTransport(API, device.deviceSecret));
          emit({ lastSyncAt: new Date().toISOString(), review: status.review + r.review, error: null, online: true });
        } catch (e) {
          const offline = e instanceof TypeError || (e as { code?: string }).code === 'offline';
          emit({ error: offline ? null : (e as Error).message, online: offline ? false : navigator.onLine });
        } finally {
          emit({ syncing: false, pending: await store.pendingCount() });
        }
      } while (again);
    } finally {
      running = false;
    }
  };

  let lastTick = 0;
  const timer = setInterval(() => {
    // Com mesas, as comandas passam entre equipamentos: sincroniza mais vezes.
    const c = getDevice().company;
    // Mesas e fila: comandas partilhadas entre equipamentos.
    const every = (c.modules ? c.modules.includes('tables') || c.modules.includes('queue') || c.modules.includes('tabs') : c.businessType === 'restaurant') ? RESTAURANT_EVERY_MS : EVERY_MS;
    if (Date.now() - lastTick >= every) { lastTick = Date.now(); void run(); }
  }, RESTAURANT_EVERY_MS);
  const onOnline = () => { emit({ online: true }); void run(); };
  const onOffline = () => emit({ online: false });
  window.addEventListener('online', onOnline);
  window.addEventListener('offline', onOffline);
  void run();
  return {
    now: () => void run(),
    stop: () => { clearInterval(timer); window.removeEventListener('online', onOnline); window.removeEventListener('offline', onOffline); },
  };
}
